<template>
  <div
    v-if="displayLocaleSwitcher"
    class="nav-mobile-locale-switcher"
    :class="{ 'nav-mobile-locale-switcher--active': open }"
  >
    <div
      class="nav-mobile-locale-switcher__active"
      @click="openLocaleSwitcher"
    >
      <div class="nav-mobile-locale-switcher__flag">
        <NavFlagIcon :iso-code="activeLocaleObject.locale" />
      </div>

      <span>{{ activeLocaleObject.language }}</span>

      <span>{{ translation.localeSwitcherChange }}</span>
    </div>

    <div
      class="nav-mobile-locale-switcher__close"
      @click="closeLocaleSwitcher"
    />

    <div class="nav-mobile-locale-switcher__list-container">
      <div class="nav-mobile-locale-switcher__list-wrapper">
        <div class="nav-mobile-locale-switcher__list-header">
          <span>{{ translation.localeSwitcherHeader }}</span>
          <span @click="closeLocaleSwitcher">
            {{ translation.localeSwitcherCancel }}
          </span>
        </div>

        <ul class="nav-mobile-locale-switcher__list">
          <li
            v-for="locale in localeNavigation"
            :key="locale.locale"
            class="nav-mobile-locale-switcher__list-item"
            @click="displayLink(locale) ? handleLinkClick() : null"
          >
            <component
              :is="displayLink(locale) ? 't3-link' : 'div'"
              :to="displayLink(locale) ? locale.localePath : null"
              class="nav-mobile-locale-switcher__list-link"
              :class="{ 'nav-mobile-locale-switcher__list-link--disabled': !locale.available }"
            >
              <NavFlagIcon :iso-code="locale.locale" />

              <span>{{ locale.language }}</span>

              <SvgIconCheck v-if="locale.active" />
            </component>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import SvgIconCheck from '~ui/assets/icons/icon-check.svg'
import type { ILocaleNavigation } from '~ui/types'

import NavFlagIcon from '../NavComponents/NavFlagIcon.vue'

interface Props {
  localeNavigation: Array<ILocaleNavigation>
  open: boolean
  translation: Record<string, string>
}

const props = defineProps<Props>()

const displayLocaleSwitcher = computed(() => {
  return props.localeNavigation.length > 1
})

const activeLocaleObject = computed(() => {
  return props.localeNavigation.find(({ active }) => active)!
})

const emit = defineEmits<{
  (event: 'link-click'): void
  (event: 'toggle', bool: boolean): void
}>()

const closeLocaleSwitcher = () => emit('toggle', false)

const handleLinkClick = () => {
  closeLocaleSwitcher()
  emit('link-click')
}

const openLocaleSwitcher = () => emit('toggle', true)

const displayLink = ({ active, available }: ILocaleNavigation) => {
  return !(active || !available)
}
</script>

<style lang="scss">
.nav-mobile-locale-switcher {
  position: relative;

  &__close {
    position: fixed;
    inset: 0;
    background-color: rgba(color(tarawera), 32%);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition:
      opacity 0.4s,
      visibility 0.4s;
  }

  &--active &__close {
    pointer-events: all;
    opacity: 1;
    visibility: visible;
  }

  &__active {
    display: flex;
    align-items: center;
    gap: rem(12px);
    background-color: color(grey-150);
    padding: rem($container-padding);
    color: color(grey-800);

    & > span:last-child {
      margin-left: auto;
    }
  }

  &__list {
    &-header {
      display: flex;
      justify-content: space-between;
      padding-inline: rem($container-padding);
      padding-block: calc(rem($container-padding) * 1.5)
        calc(rem($container-padding) / 2);

      & > span:first-child {
        font-weight: 600;
        font-size: rem(16px);
        color: color(grey-800);
      }

      & > span:last-child {
        font-weight: 500;
        font-size: rem(13px);
        text-transform: uppercase;
        color: color(grey-550);
      }
    }

    &-item {
      & span {
        color: color(dark-blue-900);
      }
    }

    &-link {
      padding: rem($container-padding);
      display: flex;
      align-items: center;
      gap: rem(12px);

      & svg {
        margin-left: auto;

        & path {
          fill: color(primary);
        }
      }

      &--disabled {
        opacity: 0.3;
      }
    }
  }

  &__list-container {
    display: grid;
    grid-template-rows: 0fr;
    align-content: end;
    position: fixed;
    bottom: var(--overlay-position, 0);
    width: 100%;
    background-color: color(white);
    border-radius: em(8px) em(8px) 0 0;
    visibility: hidden;
    overflow: hidden;
    transition:
      grid-template-rows 0.2s,
      visibility 0.2s;
  }

  &__list-wrapper {
    min-height: 0;
  }

  &--active &__list-container {
    grid-template-rows: 1fr;
    visibility: visible;
  }
}
</style>
